import "web-ui/tom-select"
import { Controller } from "web-ui/controller"
import { morphdomHold } from "web-ui/morphdom/hold"
import * as Log from "web-ui/log"

// Connects to data-controller="combo-box"
export default class extends Controller {
  static values = {
    searchPlaceholder: { type: String, default: "Search..." },
    addOptionUrl: { type: String },
    addOptionValue: { type: String },
    clearButtonEnabled: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false }
  }

  static events = {
    "turbo:before-cache@document": "beforeTurboCache"
  }

  connect() {
    this.debug("Connect")

    this.initializeTomSelect()

    if (this.autofocusValue) {
      requestAnimationFrame(() => {
        if (!this.element.isConnected) {
          return
        }

        this.tomSelect.focus()
      })
    }
  }

  initializeTomSelect() {
    const select = this.element.querySelector("select")

    this.debug("Initialize TomSelect", { selectId: select.id, searchPlaceholder: this.searchPlaceholderValue })

    if (this.tomSelect) {
      this.tomSelect.destroy()
    }

    const hasBlankOption = select.querySelectorAll("option[value='']").length > 0

    const body = document.querySelector("body")
    let dropdownParent = body
    let dropdownPlacementStrategy = "absolute"

    const dialog = select.closest("dialog")

    if (dialog) {
      dropdownParent = dialog
      dropdownPlacementStrategy = "fixed"
    }

    const plugins = {
      "no_backspace_delete": {},
      "dropdown_input": { placeholder: this.searchPlaceholderValue },
      "dropdown_placement": { strategy: dropdownPlacementStrategy },
      "dropdown_scroll": {},
      "dropdown_open": {},
      "dropdown_close_on_scroll": {},
      "turbo_morph_sync": {},
      "submit_form": {},
      "destroy": {}
    }

    if (this.clearButtonEnabledValue) {
      plugins["clear_button"] = {}
    }

    if (this.addOptionUrlValue) {
      plugins["add_option"] = {
        url: this.addOptionUrlValue,
        value: this.addOptionValueValue
      }
    }

    this.tomSelect = new TomSelect(select, {
      plugins: plugins,
      refreshThrottle: 0,
      dropdownParent,
      openOnFocus: false,
      allowEmptyOption: hasBlankOption,
      maxOptions: null,

      onFocus: () => {
        this.debug("Focus")
        this.hold ??= morphdomHold(this.element)
      },

      onBlur: () => {
        if (this.tomSelect.isFocused) {
          return
        }

        this.debug("Blur")
        this.releaseHold()
      },

      render: {
        dropdown: () => {
          return `<div id="${select.id}-dropdown"></div>`
        },
        item: (data, escape) => {
          return `<div title="${escape(data.text)}">${escape(data.text)}</div>`
        }
      }
    })

    // A Tom Select that is destroyed while focused never reports its blur, so
    // its hold would never be released. - Shaun, Wed Oct 7 2026
    this.tomSelect.on("destroy", () => {
      this.releaseHold()
    })
  }

  addOptionUrlValueChanged() {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  addOptionValueValueChanged() {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  searchPlaceholderValueChanged() {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  clearButtonEnabledValueChanged() {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  addOption = (value, text) => {
    const option = this.tomSelect.options[value]

    if (!option) {
      this.tomSelect.addOption({ value, text }, true)
      return
    }

    if (option.text !== text) {
      option.text = text
      this.tomSelect.updateOption(value, option)
    }
  }

  selectOption = (value) => {
    this.tomSelect.addItem(value)
  }

  releaseHold() {
    if (this.hold) {
      this.hold.release()
      this.hold = null
    }
  }

  beforeTurboCache() {
    this.debug("Before Turbo Cache")

    this.tomSelect.destroy()
  }

  disconnect() {
    this.debug("Disconnect")

    this.tomSelect.destroy()
  }

  debug(message, data={}) {
    Object.assign(data, {
      id: this.element.id
    })

    Log.debug("Combo Box - " + message, data)
  }
}
